import { z } from 'zod';
import { MAX_ATTENDANCE_RANGE_DAYS } from './attendance.schema.js';
import { boundedDateRange } from './payroll.schema.js';
import { WorkforceGroupQuerySchema } from '../filters/workforceGroup.js';
import { PersonNowSchema } from './personNow.schema.js';

/* ===========================================================================
 * EVERY MOVEMENT, FOR EVERYONE THE CALLER CAN SEE — GET /attendance/movements.
 *
 * The per-person Movements view lists one worker's punches; this is the same
 * list across the company (owner: "what if i want to check most recent
 * movement but for all? like today movement?"). Server-paged, because a day at
 * this client is thousands of punches.
 *
 * The filter vocabularies are the per-person view's own words — `method` is
 * `movements.method.*`, `standing` is the day's punch-card outcome, `leg` is
 * the camp/site journey label — so the two screens cannot describe one punch
 * two ways (design-rules §3.6).
 * ======================================================================== */

/** How the punch reached the record. CORRECTION is a MANUAL punch an approved correction wrote. */
export const MovementMethodSchema = z.enum([
  'FIELD',
  'TERMINAL',
  'KIOSK',
  'WEB',
  'MOBILE',
  'CORRECTION',
  'MANUAL',
  'SYSTEM',
]);
export type MovementMethodFilter = z.infer<typeof MovementMethodSchema>;

/** Whether the day used it — counted, flagged (and counted), held, rejected. */
export const MovementStandingSchema = z.enum(['counted', 'flagged', 'held', 'rejected']);
export type MovementStandingFilter = z.infer<typeof MovementStandingSchema>;

/** The journey leg: punch direction × whether the place is a camp. */
export const MovementLegFilterSchema = z.enum(['campOut', 'siteIn', 'siteOut', 'campIn', 'break']);
export type MovementLegFilter = z.infer<typeof MovementLegFilterSchema>;

export const COMPANY_MOVEMENTS_PAGE_SIZE = 50;

export const CompanyMovementsQuerySchema = z
  .object({
    /** Company-local day keys; the server resolves them in the company zone. */
    from: z.string().date(),
    to: z.string().date(),
    /** Name or code. */
    search: z.string().trim().max(120).optional(),
    /** One person — the People list's expanded row. Narrows inside the caller's reach, never past it. */
    employeeId: z.string().optional(),
    worksiteId: z.string().optional(),
    method: MovementMethodSchema.optional(),
    standing: MovementStandingSchema.optional(),
    leg: MovementLegFilterSchema.optional(),
    order: z.enum(['newest', 'oldest']).default('newest'),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(COMPANY_MOVEMENTS_PAGE_SIZE),
  })
  .superRefine(boundedDateRange(MAX_ATTENDANCE_RANGE_DAYS));
export type CompanyMovementsQuery = z.infer<typeof CompanyMovementsQuerySchema>;
export type CompanyMovementsQueryInput = z.input<typeof CompanyMovementsQuerySchema>;

/* ===========================================================================
 * PEOPLE ON ONE COMPANY DAY — GET /attendance/people-day. The Overview's
 * People list: everyone with any punch that day, one row each, newest
 * movement first. No cap and no paging: a day's people is bounded by the
 * headcount, and "the first 200 of 264" is a list that is not the list.
 * ======================================================================== */

/** Where a person is, read off their last movement of the day. */
export { PersonNowSchema, type PersonNow } from './personNow.schema.js';

export const PeopleDayQuerySchema = z.object({
  /** A company-local day key. */
  date: z.string().date(),
  /** Name, code or department — every word, somewhere (the registry's `q`). */
  q: z.string().trim().max(120).optional(),
  /** The same box under the name this endpoint took before the registry; `q` wins. */
  search: z.string().trim().max(120).optional(),
  /** "Punched at": the site or camp of their last movement. */
  worksiteId: z.string().optional(),
  /* `inTransit`, retired 2026-11-03, from an older client reads as no filter:
     it named a real state once, so refusing it would break a working link.
     Any OTHER unknown state is still refused, never a silently ignored cut. */
  now: z.preprocess((v) => (v === 'inTransit' ? undefined : v), PersonNowSchema.optional()),
  /** How their last movement was recorded. */
  method: MovementMethodSchema.optional(),
  /** Only people whose last movement is waiting on a decision. `true` is the older spelling. */
  flagged: z.enum(['1', 'true']).optional(),
  /** Workers · Staff · Remote — the one rule (`workforceGroup.ts`). */
  group: WorkforceGroupQuerySchema.optional(),
  /** One department, by id. */
  departmentId: z.string().max(64).optional(),
  /** One position, by its designation id (owner D2) — never by title. */
  designationId: z.string().max(64).optional(),
  /** The list as a file, under the same filters (merge E3). */
  format: z.enum(['json', 'excel', 'csv']).optional(),
});
export type PeopleDayQuery = z.infer<typeof PeopleDayQuerySchema>;
export type PeopleDayQueryInput = z.input<typeof PeopleDayQuerySchema>;
